import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    "X-Client-Type": "web",
  },
  withCredentials: true, // Important for sending HttpOnly cookies
});

// Store Access Token in memory
let accessToken: string | null = null;

export const getAccessToken = () => accessToken;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
};

// Initial token load from localStorage (if any, for initial page load)
// This helps maintain session across page refreshes
const initialToken = localStorage.getItem("token");
if (initialToken) {
  setAccessToken(initialToken);
}

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    if (accessToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle token expiration and automatic refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Cek jika error adalah 401, BUKAN dari endpoint login atau refresh, dan belum dicoba ulang
    console.log(originalRequest.url);
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== "auth/login" &&
      originalRequest.url !== "auth/refresh-token"
    ) {
      originalRequest._retry = true;
      try {
        // The browser automatically sends the refresh_token HttpOnly cookie
        const response = await api.post("/auth/refresh-token");

        const { token: newAccessToken } = response.data.data;

        setAccessToken(newAccessToken);
        localStorage.setItem("token", newAccessToken); // Persist token for page reloads

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // If refresh fails, clear session and redirect to login
        setAccessToken(null);
        localStorage.removeItem("token");
        // Explicitly clear the refresh_token cookie
        document.cookie =
          "refresh_token=; Max-Age=0; path=/; domain=" +
          window.location.hostname;

        // Redirect to login, but avoid infinite loops if the login page itself causes a 401
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);

export default api;
